import { faArrowDownLeftAndArrowUpRightToCenter } from '@fortawesome/pro-regular-svg-icons/faArrowDownLeftAndArrowUpRightToCenter';
import { faArrowUpRightAndArrowDownLeftFromCenter } from '@fortawesome/pro-regular-svg-icons/faArrowUpRightAndArrowDownLeftFromCenter';
import { faClockRotateLeft } from '@fortawesome/pro-regular-svg-icons/faClockRotateLeft';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { QBox, QDivider, QIconButton, QStack, QText, QTooltip } from '@captivateiq/quotient';

import { HeaderMarkIcon } from './HeaderMarkIcon';
import { useAIShellContext } from '../AIShellContext';
import { ChatHistoryDropdown } from './ChatHistoryDropdown';

const DebugToggle: React.FC<{ active: boolean; onToggle: () => void }> = ({ active, onToggle }) => (
  <QBox
    sx={{
      display: 'inline-flex',
      border: '1px solid',
      borderColor: 'border.neutralPrimary.mediumContrast',
      borderRadius: '6px',
      overflow: 'hidden',
      fontSize: '11px',
      fontWeight: 600,
    }}
  >
    <QBox
      component="button"
      sx={{
        padding: '4px 10px',
        cursor: 'pointer',
        border: 'none',
        backgroundColor: !active ? 'surface.brand.default' : 'transparent',
        color: !active ? 'neutral.primary.white' : 'text.onSurface.background.mediumContrast',
        '&:hover': !active ? {} : { backgroundColor: 'surface.navigation.hover' },
      }}
      onClick={active ? onToggle : undefined}
    >
      User
    </QBox>
    <QBox
      component="button"
      sx={{
        padding: '4px 10px',
        cursor: 'pointer',
        border: 'none',
        backgroundColor: active ? 'brand.secondary.600' : 'transparent',
        color: active ? 'neutral.primary.white' : 'text.onSurface.background.mediumContrast',
        '&:hover': active ? {} : { backgroundColor: 'surface.navigation.hover' },
      }}
      onClick={!active ? onToggle : undefined}
    >
      Debug
    </QBox>
  </QBox>
);

export const HeaderAIShell = () => {
  const { t } = useTranslation('assistant');
  const {
    toggleShell,
    openNewChat,
    isExpanded,
    toggleExpanded,
    features,
    debugMode,
    toggleDebugMode,
    currentConversation,
  } = useAIShellContext();

  const firstUserMessage = currentConversation?.messages.find((message) => message.type === 'user')?.contents;
  const expandLabel = (isExpanded ? t('button_labels.collapse') : t('button_labels.expand')) ?? '';
  const closeLabel = t('button_labels.close') ?? '';
  const newChatLabel = t('button_labels.new_chat') ?? '';
  const chatHistoryLabel = t('button_labels.chat_history') ?? '';

  const [historyAnchorEl, setHistoryAnchorEl] = useState<HTMLElement | null>(null);
  const isHistoryMenuOpen = Boolean(historyAnchorEl);

  return (
    <QStack
      direction="row"
      spacing={1.5}
      sx={{
        alignItems: 'center',
        backgroundColor: 'neutral.primary.white',
        borderBottom: '1px solid',
        borderBottomColor: 'border.neutralPrimary.mediumContrast',
        color: 'text.onSurface.background.highContrast',
        height: '60px',
        padding: '16px',
      }}
    >
      <QBox sx={{ display: 'flex', flexShrink: 0 }}>
        <HeaderMarkIcon />
      </QBox>
      <QText
        sx={{
          minWidth: 0,
          maxWidth: '150px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: '13px',
          fontWeight: 600,
          color: firstUserMessage
            ? 'text.onSurface.background.highContrast'
            : 'text.onSurface.background.mediumContrast',
        }}
      >
        {firstUserMessage ?? newChatLabel}
      </QText>
      <QBox sx={{ flexGrow: 1 }} />
      {features.isInternal && <DebugToggle active={debugMode} onToggle={toggleDebugMode} />}
      <QTooltip placement="bottom" title={newChatLabel}>
        <QIconButton
          aria-label={newChatLabel}
          icon={faPlus}
          size="small"
          onClick={openNewChat}
          // Keeps focus on the message composer instead of the button, so an in-progress
          // draft isn't blurred out from under the user when they click New Chat.
          onMouseDown={(event) => event.preventDefault()}
        />
      </QTooltip>
      <QTooltip placement="bottom" title={chatHistoryLabel}>
        <QIconButton
          aria-expanded={isHistoryMenuOpen}
          aria-haspopup="menu"
          aria-label={chatHistoryLabel}
          icon={faClockRotateLeft}
          size="small"
          onClick={(event) => setHistoryAnchorEl((prev) => (prev ? null : event.currentTarget))}
        />
      </QTooltip>
      <ChatHistoryDropdown
        anchorEl={historyAnchorEl}
        open={isHistoryMenuOpen}
        onClose={() => setHistoryAnchorEl(null)}
      />
      <QTooltip placement="bottom" title={expandLabel}>
        <QIconButton
          aria-label={expandLabel}
          icon={isExpanded ? faArrowDownLeftAndArrowUpRightToCenter : faArrowUpRightAndArrowDownLeftFromCenter}
          size="small"
          onClick={toggleExpanded}
        />
      </QTooltip>
      <QDivider flexItem orientation="vertical" sx={{ height: '18px', alignSelf: 'center' }} />
      <QTooltip placement="bottom" title={closeLabel}>
        <QIconButton aria-label={closeLabel} icon={faXmark} size="small" onClick={toggleShell} />
      </QTooltip>
    </QStack>
  );
};
